import { theme, type ThemeConfig } from 'antd';

import type { ThemeMode } from '../store/themeStore';

/**
 * Brand + semantic palette. `colorPrimary` is mirrored as `--sa-color-primary` in
 * `tokens.css` (kept in sync by hand — see the antd cssVar collision note in AGENTS/docs).
 * In `cssVar` mode Ant emits every semantic token (`--sa-color-text`, `--sa-color-bg-*`,
 * `--sa-color-border*`, `--sa-color-fill-*`, `--sa-color-error/-success/-warning`, …) and
 * **re-emits them with dark values** when the dark algorithm is active, which is what lets the
 * custom CSS in `tokens.css`/`*.module.css` flip between light and dark for free.
 */
const INDIGO = '#6366f1';
/** Slightly darker indigo for text/links on a light surface (AA contrast on white). */
const INDIGO_ON_LIGHT = '#4f46e5';
/** Brighter indigo for links on a dark surface. */
const INDIGO_ON_DARK = '#818cf8';
/** Light indigo for the selected/hovered menu item text + underline on the dark header. */
const INDIGO_ON_DARK_TEXT = '#a5b4fc';
const HEADER_DARK = '#1f2329';

export const buildAntdTheme = (mode: ThemeMode): ThemeConfig => {
  const isDark = mode === 'dark';

  return {
    cssVar: { key: 'sa' },
    algorithm: isDark ? theme.darkAlgorithm : theme.defaultAlgorithm,
    token: {
      colorPrimary: INDIGO,
      colorLink: isDark ? INDIGO_ON_DARK : INDIGO_ON_LIGHT,
      colorSuccess: '#23c3a3',
      colorError: '#e5484d',
      colorWarning: '#ffa31a',
      // Dark-mode elevation ramp. The dark algorithm's defaults sit too close together
      // (page #000 vs card #141414), so blocks barely separate from the page. Give the page a
      // soft near-black and lift the card/elevated surfaces so content blocks clearly stand out:
      //   page #0f1115  <  cards #2f3641  <  modals #363d4a  <  card cover #282c34.
      // Borders are also lifted well above the surfaces so block edges read crisply.
      // NOTE: the strong border (`colorBorder`: #586074 dark / #d9d9d9 light) is mirrored by
      // `--sa-border-subtle` in tokens.css for elements outside an antd cssVar scope (the Score
      // Breakdown modal sections). Keep the two in sync — same pattern as colorPrimary.
      ...(isDark
        ? {
            colorBgLayout: '#0f1115',
            colorBgContainer: '#2f3641',
            colorBgElevated: '#363d4a',
            colorBorder: '#586074',
            colorBorderSecondary: '#4a5262',
          }
        : {
            // antd's default card border (#f0f0f0) is invisible against white cards on the
            // light-gray page; lift it to match the top buttons' border so blocks read crisply.
            colorBorder: '#d9d9d9',
            colorBorderSecondary: '#d9d9d9',
          }),
    },
    components: {
      Layout: {
        headerBg: isDark ? HEADER_DARK : '#ffffff',
      },
      // The Menu always uses antd's light rendering path (Menu has no `theme="dark"` prop) so the
      // selected item is a clean indigo text + underline in BOTH modes — the dark-theme path
      // renders a filled block instead of the underline. Colors differ per mode: on the dark
      // header, non-selected items need light text and the accent is a lighter indigo.
      Menu: {
        itemBg: 'transparent',
        itemSelectedBg: 'transparent',
        itemSelectedColor: isDark ? INDIGO_ON_DARK_TEXT : INDIGO_ON_LIGHT,
        itemHoverColor: isDark ? INDIGO_ON_DARK_TEXT : INDIGO_ON_LIGHT,
        horizontalItemSelectedColor: isDark ? INDIGO_ON_DARK_TEXT : INDIGO_ON_LIGHT,
        ...(isDark
          ? {
              itemColor: 'rgba(255, 255, 255, 0.72)',
              popupBg: HEADER_DARK,
              subMenuItemBg: HEADER_DARK,
            }
          : {}),
      },
      // Sliders: antd's algorithm-derived track/handle read too faint in BOTH themes for the
      // score/price filters. Use a solid indigo selected track + clearly visible handle ring and
      // rail. Mark dots stay faint/neutral (not indigo) so they read as quiet reference points and
      // don't get confused with the draggable handles.
      Slider: {
        trackBg: INDIGO,
        trackHoverBg: isDark ? INDIGO_ON_DARK : INDIGO_ON_LIGHT,
        railBg: isDark ? 'rgba(255, 255, 255, 0.16)' : 'rgba(0, 0, 0, 0.12)',
        railHoverBg: isDark ? 'rgba(255, 255, 255, 0.24)' : 'rgba(0, 0, 0, 0.2)',
        handleColor: isDark ? INDIGO_ON_DARK : INDIGO,
        handleActiveColor: isDark ? INDIGO_ON_DARK_TEXT : INDIGO_ON_LIGHT,
        dotBorderColor: isDark ? 'rgba(255, 255, 255, 0.18)' : 'rgba(0, 0, 0, 0.15)',
        dotActiveBorderColor: isDark ? 'rgba(255, 255, 255, 0.35)' : 'rgba(0, 0, 0, 0.28)',
      },
      // The tab-bar baseline reads colorBorderSecondary, which we lifted for crisp CARD borders —
      // too heavy here. Soften it back to a faint hairline (per-component override) so the tabs
      // keep the light, underline-only look from the original design.
      Tabs: {
        colorBorderSecondary: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)',
      },
    },
  };
};
